/**
 * CierreFacturacionModal — modal unificado para marcar/editar cierre o
 * facturación de una OC y su WO espejo. Reemplaza CerrarOTModal,
 * EditarCierreOTModal y MarcarFacturadaModal.
 *
 * Props:
 *   target      { co?, wo? }  Pasá la CO o la WO (o ambas). El modal
 *                              completa lo que falta del lado opuesto.
 *   onClose     ()            cerrar sin guardar.
 *   onSaved     (data)        callback con { workOrder, customerOrder } resultado.
 *
 * Lógica:
 *   - Si la WO no está cerrada → POST /workOrder/:id/cerrar (alta inicial).
 *   - Si la WO ya está cerrada → PATCH /workOrder/:id/editar-cierre.
 *   - Si solo hay CO sin WO → PATCH /customerOrder/:id con invoicedAt/closedAt.
 *   - Las hooks del backend cascadean automático: WO ↔ CO ↔ PieceInstances.
 *
 * Diseño con escalabilidad en mente:
 *   - Un solo bundle, importable desde cualquier pantalla.
 *   - Sin estado global, sin context — solo props.
 *   - Componente puro / barato de montar/desmontar.
 */
import React, { useEffect, useState } from "react";
import backend from "../../backend";

const DOC_TYPES = [
  { value: "factura",      label: "Factura"          },
  { value: "guia",         label: "Guía de despacho" },
  { value: "boleta",       label: "Boleta"           },
  { value: "nota_credito", label: "Nota de crédito"  },
];

const REVERSE_TYPE = {
  factura_electronica: "factura",
  boleta:              "boleta",
  guia_despacho:       "guia",
  nota_credito:        "nota_credito",
};

// Mapeo doctype del modal → docType del Document model.
// Cuando se adjunte un PDF, se sube con este docType y workOrderRefs=woId.
const DOC_TYPE_TO_DOCUMENT = {
  factura:      "facturaEmitida",
  guia:         "guiaDespacho",
  boleta:       "boleta",
  nota_credito: "notaCredito",
};

const isClosed = wo => wo && ["delivered", "facturado", "cancelled"].includes(wo.status);

const toDateInput = iso => {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
};

const toIsoNoonUTC = ymd => {
  if (!ymd) return null;
  return new Date(ymd + "T12:00:00Z").toISOString();
};

const CierreFacturacionModal = ({ target, onClose, onSaved }) => {
  const co = target?.co || null;
  const wo = target?.wo || null;
  const woClosed = isClosed(wo);
  const coInvoiced = !!co?.invoicedAt;
  const isEditing = woClosed || coInvoiced;

  // Inferir tipo doc actual
  const initialType = (() => {
    if (wo?.documentoTributario?.tipo) return REVERSE_TYPE[wo.documentoTributario.tipo] || "factura";
    if (wo?.guiaDespacho) return "guia";
    if (wo?.factura) return "factura";
    return "factura";
  })();

  const initialNumber = (() => {
    return wo?.documentoTributario?.numero
        || wo?.factura
        || wo?.guiaDespacho
        || "";
  })();

  const initialFechaFact = (() => {
    return toDateInput(
      wo?.documentoTributario?.fecha
      || co?.invoicedAt
      || new Date()
    );
  })();

  const initialFechaCierre = toDateInput(co?.closedAt);

  const [docType,      setDocType]      = useState(initialType);
  const [docNumber,    setDocNumber]    = useState(initialNumber);
  const [fechaFact,    setFechaFact]    = useState(initialFechaFact);
  const [fechaCierre,  setFechaCierre]  = useState(initialFechaCierre);
  const [unmark,       setUnmark]       = useState(false);
  const [advanced,     setAdvanced]     = useState(false);
  const [pdfFile,      setPdfFile]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [error,        setError]        = useState("");
  // D-Producción-Stock — capturar movimientos de stock que vinieron del backend
  const [closeResult,  setCloseResult]  = useState(null); // { stockMovimientos: [...] }

  // Re-init cuando cambia el target
  useEffect(() => {
    if (!target) return;
    setDocType(initialType);
    setDocNumber(initialNumber);
    setFechaFact(initialFechaFact);
    setFechaCierre(initialFechaCierre);
    setUnmark(false);
    setAdvanced(false);
    setPdfFile(null);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [co?._id, wo?._id]);

  if (!target) return null;

  const headerTitle = (() => {
    if (unmark) return "Desmarcar facturación";
    if (isEditing) return "Editar facturación";
    return "Marcar como facturada";
  })();

  const headerSubtitle = (() => {
    const ocNum = co?.clientOCNumber || "";
    const woNum = wo?.number || co?.workOrders?.[0]?.number;
    const cliente = co?.client?.name || wo?.client?.name || "—";
    const parts = [];
    if (ocNum) parts.push(`OC ${ocNum}`);
    if (woNum) parts.push(`OT N° ${woNum}`);
    parts.push(cliente);
    return parts.join(" · ");
  })();

  const submit = async () => {
    if (!unmark) {
      if (!fechaFact) { setError("Ingresa la fecha de facturación"); return; }
      if (!docNumber || !String(docNumber).trim()) {
        setError("Ingresa el N° de documento");
        return;
      }
    }
    setSaving(true); setError("");
    try {
      let result = { workOrder: null, customerOrder: null };

      if (unmark) {
        // Desmarcar: solo aplica si CO existe (la WO mirror no puede "desfacturarse" sin reabrir)
        if (co?._id) {
          const r = await backend.patch(`customerOrder/${co._id}`, {
            invoicedAt: null, closedAt: null,
          });
          result.customerOrder = r.data;
        }
      } else if (wo?._id) {
        // Hay WO → llamar endpoint WO (cerrar nuevo o editar existente).
        // El endpoint propaga a CO via hooks/syncCoStatusFromWos.
        const body = {
          docType,
          docNumber: String(docNumber).trim(),
          docDate: toIsoNoonUTC(fechaFact),
        };
        const url = woClosed
          ? `workOrder/${wo._id}/editar-cierre`
          : `workOrder/${wo._id}/cerrar`;
        const r = await backend.patch(url, body);
        result.workOrder = r.data?.workOrder || r.data;
        // D-Producción-Stock — capturar movimientos de stock material para mostrarlos
        if (Array.isArray(r.data?.stockMovimientos)) {
          result.stockMovimientos = r.data.stockMovimientos;
        }

        // Si el usuario seteó closedAt distinto de fechaFact, hacer un PATCH a CO
        if (co?._id && fechaCierre && fechaCierre !== fechaFact) {
          const r2 = await backend.patch(`customerOrder/${co._id}`, {
            closedAt: toIsoNoonUTC(fechaCierre),
          });
          result.customerOrder = r2.data;
        }
      } else if (co?._id) {
        // Solo CO → PATCH directo (sin folio)
        const r = await backend.patch(`customerOrder/${co._id}`, {
          invoicedAt: toIsoNoonUTC(fechaFact),
          closedAt:   toIsoNoonUTC(fechaCierre || fechaFact),
        });
        result.customerOrder = r.data;
      }

      // Si hay archivo adjunto y tenemos WO, subirlo via /document/upload
      // (después del cierre exitoso para no dejar archivos huérfanos si falla).
      const finalWoId = result.workOrder?._id || wo?._id;
      if (!unmark && pdfFile && finalWoId) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", pdfFile);
          fd.append("docType", DOC_TYPE_TO_DOCUMENT[docType] || "otro");
          fd.append("workOrderRefs", finalWoId);
          fd.append("label", `Folio ${String(docNumber).trim()}`);
          await backend.post("document/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          // Cierre OK pero subida falló: avisamos y dejamos cerrar igual.
          alert(
            "Cierre guardado correctamente, pero falló la subida del PDF: " +
            (uploadErr?.response?.data?.error || uploadErr.message) +
            ".\nPodés subirlo después desde el tab Documentos de la OT."
          );
        }
        setUploading(false);
      }

      onSaved?.(result);
      // Si hubo movimientos de stock material, mostrar pantalla de confirmación
      // antes de cerrar; sino cerrar directo (UX igual que antes).
      if (result.stockMovimientos?.length > 0) {
        setCloseResult({ stockMovimientos: result.stockMovimientos });
      } else {
        onClose();
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
    setSaving(false);
  };

  const accentColor = unmark ? "#e74c3c" : isEditing ? "#3498db" : "#2ecc71";

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#1e1e1e", border: "1px solid #2e2e2e", borderRadius: 8,
        padding: 18, width: "min(460px, 94vw)", maxHeight: "90vh", overflowY: "auto",
        color: "#ddd", fontSize: 12,
        fontFamily: "Arial, sans-serif",
      }}>
        {/* D-Producción-Stock — pantalla de éxito post-cierre con movimientos de material */}
        {closeResult ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#2ecc71", marginBottom: 4 }}>
              ✓ Cierre registrado
            </div>
            <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>{headerSubtitle}</div>
            <div style={{
              background: "#0a1f15", border: "1px solid #1a3a25",
              borderRadius: 6, padding: "10px 12px", marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, color: "#e67e22", fontWeight: 700, marginBottom: 6 }}>
                🪵 Material descontado del inventario:
              </div>
              {closeResult.stockMovimientos.map((m, i) => (
                <div key={i} style={{ fontSize: 12, color: "#ddd", paddingLeft: 8, marginTop: 2 }}>
                  • <strong>{m.materialName}</strong>: <span style={{ color: "#e74c3c" }}>−{m.qty} {m.unit}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{
                background: "#2ecc71", color: "#0a1f15",
                border: "none", borderRadius: 5,
                padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>Continuar</button>
            </div>
          </>
        ) : (<>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{headerTitle}</div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>{headerSubtitle}</div>

        {!unmark && (
          <>
            <div style={{ marginBottom: 10 }}>
              <Label>Tipo de documento</Label>
              <select value={docType} onChange={e => setDocType(e.target.value)} style={INPUT}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 8, marginBottom: 10 }}>
              <div>
                <Label>N° de documento</Label>
                <input type="text" value={docNumber}
                  onChange={e => setDocNumber(e.target.value)}
                  placeholder="Ej: 12345" autoFocus
                  style={{ ...INPUT, fontFamily: "monospace" }}
                />
              </div>
              <div>
                <Label>Fecha facturación</Label>
                <input type="date" value={fechaFact}
                  onChange={e => setFechaFact(e.target.value)}
                  style={{ ...INPUT, fontFamily: "monospace" }}
                />
              </div>
            </div>

            {/* Avanzado: fecha cierre distinta de fecha facturación (caso raro) */}
            <button type="button"
              onClick={() => setAdvanced(a => !a)}
              style={{
                background: "transparent", border: "none", color: "#3498db",
                fontSize: 10, cursor: "pointer", padding: 0, marginBottom: 8,
              }}
            >{advanced ? "▼" : "▶"} Avanzado</button>

            {advanced && (
              <div style={{ marginBottom: 10 }}>
                <Label>Fecha cierre <span style={{ textTransform: "none", color: "#999" }}>(opcional, default = facturación)</span></Label>
                <input type="date" value={fechaCierre}
                  onChange={e => setFechaCierre(e.target.value)}
                  style={{ ...INPUT, fontFamily: "monospace" }}
                />
              </div>
            )}

            {/* Adjuntar PDF del documento */}
            <div style={{ marginBottom: 12 }}>
              <Label>📎 Adjuntar PDF del documento <span style={{ textTransform: "none", color: "#999" }}>(opcional)</span></Label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{
                  flex: 1, padding: "7px 10px",
                  background: "#161616", border: "1px dashed #444",
                  borderRadius: 5, color: pdfFile ? "#2ecc71" : "#888",
                  fontSize: 11, cursor: "pointer", textAlign: "center",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  <input type="file"
                    accept="application/pdf,image/*"
                    onChange={e => setPdfFile(e.target.files?.[0] || null)}
                    style={{ display: "none" }}
                  />
                  {pdfFile ? `✓ ${pdfFile.name}` : "Seleccionar archivo (PDF, imagen)"}
                </label>
                {pdfFile && (
                  <button type="button" onClick={() => setPdfFile(null)}
                    style={{
                      background: "transparent", color: "#cbd5e1",
                      border: "1px solid #444", borderRadius: 5,
                      padding: "5px 9px", fontSize: 11, cursor: "pointer",
                    }}
                  >×</button>
                )}
              </div>
              <div style={{ fontSize: 9, color: "#999", marginTop: 4 }}>
                Se sube como tipo "{DOC_TYPE_TO_DOCUMENT[docType] || "otro"}" vinculado a la OT.
                Cuando se active SII, este paso se hace automático con el PDF descargado del DTE.
              </div>
            </div>

            <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 14, lineHeight: 1.4 }}>
              Al guardar: la OC pasa a <b>facturada</b> en {fechaFact || "fecha"}; la OT espejo a
              {" "}<b>{docType === "guia" ? "delivered" : "facturado"}</b> con
              <code> documentoTributario.{`{tipo,numero,fecha}`}</code>; los items
              quedan como <b>invoiced</b>. Cambios afectan el reporte mensual de /produccion.
            </div>
          </>
        )}

        {isEditing && (
          <label style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: 14,
            fontSize: 11, color: unmark ? "#e74c3c" : "#888", cursor: "pointer",
          }}>
            <input type="checkbox" checked={unmark} onChange={e => setUnmark(e.target.checked)} />
            Desmarcar facturación (volver a "en proceso")
          </label>
        )}

        {error && (
          <div style={{
            background: "#2d0000", border: "1px solid #4a0000",
            color: "#e74c3c", padding: "6px 10px", borderRadius: 5,
            fontSize: 11, marginBottom: 12,
          }}>{error}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{
            background: "transparent", color: "#cbd5e1", border: "1px solid #444",
            borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer",
          }}>Cancelar</button>
          <button onClick={submit} disabled={saving || uploading} style={{
            background: accentColor,
            color: unmark || !isEditing ? "#fff" : "#0d1f2e",
            border: "none", borderRadius: 5, padding: "6px 14px",
            fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: (saving || uploading) ? .7 : 1,
          }}>
            {uploading ? "Subiendo PDF…"
              : saving    ? "Guardando…"
              : unmark    ? "Desmarcar"
              : isEditing ? "✎ Actualizar"
              : "✓ Marcar facturada"}
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".05em" }}>
    {children}
  </div>
);

const INPUT = {
  width: "100%", background: "#161616", border: "1px solid #333",
  borderRadius: 5, padding: "7px 10px", color: "#ddd", fontSize: 12,
  boxSizing: "border-box",
};

export default CierreFacturacionModal;
